import { is } from '@common/is/is'
import { Number } from '@common/Number/Number'
import Text from '@common/components/Text/Text'
import { Semver } from '@common/Semver/Semver'

import DOM from '@common/DOM/DOM'

import { NAV_EVENT, ENavigator } from '../../navigator'

import '../ButtonContainer/ButtonContainer'
import '../Welcome/Welcome'
import '../Congratulations/Congratulations'
import '../Activated/Activated'
import '../Licence/Licence'
import '../Activate/Activate'
import '../Deactivate/Deactivate'
import '../Button/Button'
import '../Logo/Logo'

import activator from '../../activator'

import getComponent from './utils/getComponent'
import openExternalUrl from './utils/openExternalUrl'

import styles from './BaseActivator.scss'

export const ACTIVATOR_START_EVENT = 'Activator:start'

export default abstract class BaseActivator extends HTMLElement {
  private get version (): string {
    return this.getAttribute('version') ?? ''
  }

  private get serial (): string {
    return this.getAttribute('serial') ?? ''
  }

  private get content (): HTMLElement {
    return this.querySelector('.activator__content') as HTMLElement
  }

  /** Demó verzió indítása. */
  public startDemo (): void {
    activator.startDemo()
    this.ready()
  }

  /** Server request, hogy az aktuális verziónál van-e frissebb verzió. */
  public checkUpdate (): void {
    // eslint-disable-next-line no-restricted-syntax -- A .then így maradjon.
    activator.checkUpdate(true)
      .then((updateData) => {
        if (
          activator.isActivated() &&
          (is.defined(updateData.valid_for) && updateData.valid_for === 0) &&
          (is.defined(updateData.reason) && Number.parse(updateData.reason) > 0)
        ) {
          activator.deactivate(this.onNavigate)
        }
        else if (
          Semver.gt(
            Semver.coerce(updateData.version) ?? '',
            Semver.coerce(this.version) ?? ''
          ) &&
          is.string(updateData.filename)
        ) {
          this.showNewVersion(updateData.filename)
        }

        this.onNavigate()
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.log(error)
        this.onNavigate()
      })
  }

  public connectedCallback (): void {
    activator.init(this.serial)
    document.addEventListener(NAV_EVENT, this.onNavigate)
  }

  /**
   * Ha van új verzió, akkor figyelmeztetjük róla a felhasználót.
   * @param newVersionFileName - Az új verziónak a fájlneve.
   */
  private showNewVersion (newVersionFileName: string): void {
    const versionEl = this.querySelector('.activator__version') as HTMLElement

    const updateLink = `https://activator.balazs-diak.hu/products/${ newVersionFileName }`

    const linkNode: HTMLElement = (
      <a className={ styles.versionButton } href={ updateLink }>
        <Text.instance key="activator.newVersion" />
      </a>
    )

    linkNode.addEventListener('click', (e: Event) => {
      e.preventDefault()

      openExternalUrl(updateLink)
    })

    versionEl.appendChild(
      <div className={ styles.versionButtonContainer }>
        { linkNode }
      </div>
    )
  }

  /**
   * Navigálás callback-je.
   * @param e - Az esemény, ha van.
   */
  private readonly onNavigate = (e = {}): void => {
    const c = this.content

    c.innerHTML = ''

    const { detail } = e as CustomEvent

    // Elkéri a beszúrandó komponenst.
    const Component = getComponent(detail)

    // Ha ez nem null, akkor beszúrja.
    if (Component) {
      c.appendChild(<Component />)

      return
    }

    // Különben pedig további logika.
    if (detail === ENavigator.Demo) {
      this.startDemo()
    }

    if (detail === ENavigator.Start) {
      this.ready()
    }

    c.appendChild(<a-welcome />)
  }

  /**
   * Ezt a függvényt a startDemo és a rendes, aktiválás utáni start is hívja.
   * Kilövünk egy eventet, aminek hatására tűnik majd el az activator és kezdődik az app.
   */
  private readonly ready = (): void => {
    const isDemo = activator.isDemo()

    DOM.dispatch(this, ACTIVATOR_START_EVENT, true, { isDemo })
  }
}
